import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, Space, Statistic, Grid, Modal, Checkbox, Alert } from 'antd';
import { PhoneOutlined, SafetyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { SelfRegistrationSessionDTO, SelfRegistrationRequestDTO } from '@/shared/api/model';
import { 
    useSelfRegistration
} from '../../hooks/useSelfRegistration';
import { showSuccessNotification } from '@/components/common/utils/notify';
import { getCompanyDisplayName, useCompany } from '@/hooks/company/use-company';

const { Title, Text } = Typography;
const { Countdown } = Statistic;
const { useBreakpoint } = Grid;
const PHONE_OTP_RESEND_COOLDOWN_MS = 20000;
const PHONE_OTP_EXPIRY_MS = 300000;

const getRemainingCountdown = (baseTime?: string, durationMs?: number): number | null => {
    if (!baseTime || !durationMs) {
        return null;
    }

    const baseTimestamp = new Date(baseTime).getTime();
    if (Number.isNaN(baseTimestamp)) {
        return null;
    }

    const remaining = baseTimestamp + durationMs - Date.now();
    return remaining > 0 ? Date.now() + remaining : null;
};

const normalizeFullName = (value?: string) => {
    return (value || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part.toUpperCase())
        .join(' ');
};

interface PhoneVerificationStepProps {
    session: SelfRegistrationSessionDTO | null;
    onComplete: (session: SelfRegistrationSessionDTO) => void;
    onError: (message: string) => void;
}

const PhoneVerificationStep: React.FC<PhoneVerificationStepProps> = ({
    session,
    onComplete,
    onError
}) => {
    const [form] = Form.useForm();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { data: companyData } = useCompany();
    const institutionName = getCompanyDisplayName(companyData);
    
    console.log('🔍 Component initialization - session:', session);
    console.log('🔍 Component initialization - !!session:', !!session);
    const [otpSent, setOtpSent] = useState(!!session);
    console.log('🔍 Component initialization - otpSent initial value:', !!session);
    const [canResend, setCanResend] = useState(false);
    const [resendCountdown, setResendCountdown] = useState<number | null>(null);
    const [otpExpiryCountdown, setOtpExpiryCountdown] = useState<number | null>(null);
    const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
    const [accountAlreadyCreated, setAccountAlreadyCreated] = useState(false);

    // Use the refactored self registration hook
    const {
        initiateRegistration,
        verifyPhone,
        resendPhoneOtp,
        isInitiating,
        isVerifyingPhone,
        isResendingPhoneOtp
    } = useSelfRegistration();

    // Track otpSent changes
    useEffect(() => {
        console.log('🔍 otpSent changed to:', otpSent);
    }, [otpSent]);

    useEffect(() => {
        console.log('🔍 useEffect [session] triggered - session:', session);
        if (session && !session.phoneVerified) {
            console.log('✅ Setting otpSent to true because session exists and phone not verified');
            setOtpSent(true);
            if (resendCountdown === null) {
                const sessionBaseTime = session.updatedAt || session.createdAt;
                const nextResendCountdown = getRemainingCountdown(sessionBaseTime, PHONE_OTP_RESEND_COOLDOWN_MS);
                setResendCountdown(nextResendCountdown);
                setCanResend(nextResendCountdown === null);
            }
            if (otpExpiryCountdown === null) {
                const sessionBaseTime = session.updatedAt || session.createdAt;
                setOtpExpiryCountdown(getRemainingCountdown(sessionBaseTime, PHONE_OTP_EXPIRY_MS));
            }
        } else if (!session) {
            setOtpSent(false);
            setCanResend(false);
            setResendCountdown(null);
            setOtpExpiryCountdown(null);
        } else {
            console.log('⚠️ Not setting otpSent - session:', session, 'phoneVerified:', session?.phoneVerified);
        }
    }, [session?.sessionToken, session?.phoneVerified, session?.updatedAt, session?.createdAt]);

    const handleInitiateRegistration = async (values: any) => {
        console.log('🚀 handleInitiateRegistration called with values:', values);
        try {
            if (!values.termsRead) {
                onError('Please read the terms and conditions before starting registration');
                return;
            }

            const normalizedFullName = normalizeFullName(values.fullName);

            const request: SelfRegistrationRequestDTO = {
                phoneNumber: values.phoneNumber,
                fullName: normalizedFullName,
                email: values.email,
                termsAccepted: false // Will be handled in next step
            };

            form.setFieldsValue({ fullName: normalizedFullName });

            console.log('🚀 Calling initiateRegistration API with request:', request);
            // Call the actual API using the refactored hook and await the response
            const sessionData = await initiateRegistration(request);
            console.log('✅ Registration initiated successfully, session data:', sessionData);
            
            // Backend now handles resume logic securely - always returns PHONE_VERIFICATION step
            // and preserves previous progress internally
            setOtpSent(true);
            setResendCountdown(Date.now() + PHONE_OTP_RESEND_COOLDOWN_MS);
            setOtpExpiryCountdown(Date.now() + PHONE_OTP_EXPIRY_MS);
            setCanResend(false);
            showSuccessNotification('Verification code sent to your phone');
            
            // Update parent component with session data
            onComplete(sessionData);
            
        } catch (error: any) {
            console.error('❌ Error in handleInitiateRegistration:', error);
            const errorMessage = error.message || 'Failed to initiate registration';
            if (errorMessage.includes('already been created') || errorMessage.includes('already used')) {
                setAccountAlreadyCreated(true);
            } else {
                onError(errorMessage);
            }
        }
    };

    const handleVerifyOtp = async (values: any) => {
        console.log('🔍 handleVerifyOtp called with values:', values);
        console.log('🔍 Current session:', session);
        console.log('🔍 verifyPhone function:', verifyPhone);
        
        if (!session) {
            console.error('❌ No session available');
            return;
        }
        
        try {
            if (!session.sessionToken) {
                throw new Error('Session token is required');
            }

            const request = {
                sessionToken: session.sessionToken,
                otpCode: values.otpCode
            };

            console.log('🔍 Verify phone request:', request);
            console.log('🚀 Calling verifyPhone API...');

            // Call the actual API using the refactored hook
            const updatedSession = await verifyPhone(request);
            console.log('✅ Phone verification successful:', updatedSession);
            
            // Backend now handles resume logic - will return the appropriate next step
            showSuccessNotification('Phone number verified successfully');
            onComplete(updatedSession);
            
        } catch (error: any) {
            console.error('❌ Error verifying phone:', error);
            // Extract the actual error message from the error object
            const errorMessage = error.message || 'Invalid OTP code';
            onError(errorMessage);
        }
    };

    const handleResendOtp = async () => {
        if (!session || !canResend) return;
        
        try {
            if (!session.sessionToken) {
                throw new Error('Session token is required');
            }
            
            // Call the actual API using the refactored hook
            await resendPhoneOtp(session.sessionToken);
            setResendCountdown(Date.now() + PHONE_OTP_RESEND_COOLDOWN_MS);
            setOtpExpiryCountdown(Date.now() + PHONE_OTP_EXPIRY_MS);
            setCanResend(false);
            showSuccessNotification('Verification code resent to your phone');
            
        } catch (error: any) {
            // Extract the actual error message from the error object
            const errorMessage = error.message || 'Failed to resend OTP';
            onError(errorMessage);
        }
    };

    const onCountdownFinish = () => {
        setCanResend(true);
        setResendCountdown(null);
    };

    console.log('🔍 Render check - otpSent:', otpSent);
    console.log('🔍 Render check - session:', session);
    
    // Show initial registration form if no session exists
    if (!session) {
        console.log('📝 Rendering initial registration form (no session)');
        if (accountAlreadyCreated) {
            return (
                <div style={{ textAlign: 'center', padding: isMobile ? '20px 0' : '40px 0' }}>
                    <CheckCircleOutlined style={{ fontSize: isMobile ? '48px' : '64px', color: '#52c41a', marginBottom: 16 }} />
                    <Title level={isMobile ? 3 : 2} style={{ color: '#52c41a' }}>Account Already Created</Title>
                    <Alert
                        message="Your registration is complete"
                        description={
                            <div>
                                <p style={{ marginBottom: 8 }}>
                                    An account for this phone number or email already exists.
                                    Please <strong>check your email or phone for your login credentials</strong>.
                                </p>
                                <p style={{ marginBottom: 0 }}>
                                    If you did not receive your credentials or need assistance, please contact our support team.
                                </p>
                            </div>
                        }
                        type="success"
                        showIcon
                        style={{ textAlign: 'left', marginBottom: 24 }}
                    />
                    <Button
                        type="primary"
                        size={isMobile ? 'middle' : 'large'}
                        href="/login"
                        style={{ marginRight: 12 }}
                    >
                        Go to Login
                    </Button>
                    <Button
                        size={isMobile ? 'middle' : 'large'}
                        onClick={() => setAccountAlreadyCreated(false)}
                    >
                        Try Different Details
                    </Button>
                </div>
            );
        }

        return (
            <div>
                <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '30px' }}>
                    <PhoneOutlined style={{ 
                        fontSize: isMobile ? '36px' : '48px', 
                        color: '#1890ff', 
                        marginBottom: isMobile ? '12px' : '16px' 
                    }} />
                    <Title level={isMobile ? 4 : 3}>Start Your Registration</Title>
                    <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                        Enter your details to begin the registration process
                    </Text>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleInitiateRegistration}
                    size={isMobile ? 'middle' : 'large'}
                >
                    <Form.Item
                        name="fullName"
                        label="Full Name"
                        tooltip={"As it appears on your salary slip.\n Example as John John Mdoe"}
                        rules={[
                            { required: true, message: 'Please enter your full name' },
                            { min: 2, message: 'Name must be at least 2 characters' }
                        ]}
                    >
                        <Input
                            placeholder="Enter your full name"
                            onBlur={(event) => {
                                const normalized = normalizeFullName(event.target.value);
                                form.setFieldsValue({ fullName: normalized });
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="phoneNumber"
                        label="Phone Number"
                        rules={[
                            { required: true, message: 'Please enter your phone number' },
                            { pattern: /^[+]?[0-9]{10,15}$/, message: 'Please enter a valid phone number' }
                        ]}
                    >
                        <Input 
                            placeholder="+255 XXX XXX XXX" 
                            style={{ fontSize: isMobile ? '16px' : '14px' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="Email Address"
                        rules={[
                            { required: true, message: 'Please enter your email address' },
                            { type: 'email', message: 'Please enter a valid email address' }
                        ]}
                    >
                        <Input 
                            placeholder="your.email@example.com" 
                            style={{ fontSize: isMobile ? '16px' : '14px' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="termsRead"
                        valuePropName="checked"
                        rules={[
                            {
                                validator: (_, value) =>
                                    value ? Promise.resolve() : Promise.reject(new Error('You must read the terms and conditions'))
                            }
                        ]}
                    >
                        <Checkbox>
                            I have read the{' '}
                            <Button type="link" onClick={() => setIsTermsModalVisible(true)} className="p-0">
                                Terms and Conditions
                            </Button>
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                (Application fee is non-refundable)
                            </Text>
                        </Checkbox>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={isInitiating}
                            block
                            size={isMobile ? 'middle' : 'large'}
                            style={{ 
                                height: isMobile ? '44px' : '48px',
                                fontSize: isMobile ? '16px' : '18px'
                            }}
                        >
                            Send Verification Code
                        </Button>
                    </Form.Item>
                </Form>

                <Modal
                    title={`${institutionName} Terms and Conditions`}
                    open={isTermsModalVisible}
                    onOk={() => setIsTermsModalVisible(false)}
                    onCancel={() => setIsTermsModalVisible(false)}
                    width={800}
                    footer={[
                        <Button key="close" type="primary" onClick={() => setIsTermsModalVisible(false)}>
                            Close
                        </Button>
                    ]}
                >
                    <div className="space-y-4 max-h-96 overflow-y-auto p-2">
                        <Typography.Title level={5}>1. Acceptance of Terms</Typography.Title>
                        <Typography.Paragraph>
                            By registering for a {institutionName} account, you agree to be bound by these terms and conditions.
                        </Typography.Paragraph>

                        <Typography.Title level={5}>2. Eligibility</Typography.Title>
                        <Typography.Paragraph>
                            You must be at least 18 years old and provide accurate and complete information during registration.
                        </Typography.Paragraph>

                        <Typography.Title level={5}>3. Account Security</Typography.Title>
                        <Typography.Paragraph>
                            You are responsible for maintaining the security of your account credentials.
                        </Typography.Paragraph>

                        <Typography.Title level={5}>4. Fees</Typography.Title>
                        <Typography.Paragraph>
                            Application fees, where applicable, are non-refundable.
                        </Typography.Paragraph>

                        <Typography.Title level={5}>4. Privacy and Data Protection</Typography.Title>
                        <Typography.Paragraph>
                            We collect and process your personal data in accordance with applicable privacy laws.
                        </Typography.Paragraph>
                    </div>
                </Modal>
            </div>
        );
    }

    console.log('📱 Rendering OTP verification form');
    return (
        <div>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '30px' }}>
                <SafetyOutlined style={{ 
                    fontSize: isMobile ? '36px' : '48px', 
                    color: '#52c41a', 
                    marginBottom: isMobile ? '12px' : '16px' 
                }} />
                <Title level={isMobile ? 4 : 3}>Verify Your Phone</Title>
                <Text type="secondary" style={{ 
                    fontSize: isMobile ? '14px' : '16px',
                    display: 'block',
                    wordBreak: 'break-word'
                }}>
                    We've sent a verification code to {session?.phoneNumber}
                </Text>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={(values) => {
                    console.log('📝 Form onFinish triggered with values:', values);
                    handleVerifyOtp(values);
                }}
                size={isMobile ? 'middle' : 'large'}
            >
                <Form.Item
                    name="otpCode"
                    label="Verification Code"
                    rules={[
                        { required: true, message: 'Please enter the verification code' },
                        { len: 6, message: 'Verification code must be 6 digits' }
                    ]}
                >
                    <Input
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        style={{ 
                            textAlign: 'center', 
                            fontSize: isMobile ? '20px' : '18px', 
                            letterSpacing: isMobile ? '3px' : '4px',
                            height: isMobile ? '48px' : '40px'
                        }}
                    />
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isVerifyingPhone}
                        block
                        size={isMobile ? 'middle' : 'large'}
                        style={{ 
                            height: isMobile ? '44px' : '48px',
                            fontSize: isMobile ? '16px' : '18px'
                        }}
                        onClick={() => {
                            console.log('🔘 Verify Phone Number button clicked!');
                            console.log('🔘 Form values:', form.getFieldsValue());
                        }}
                    >
                        Verify Phone Number
                    </Button>
                </Form.Item>

                <div style={{ textAlign: 'center', marginTop: isMobile ? '15px' : '20px' }}>
                    {resendCountdown !== null ? (
                        <Space direction={isMobile ? 'vertical' : 'horizontal'} size="small">
                            <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                Resend code in:
                            </Text>
                            <Countdown
                                value={resendCountdown}
                                format="mm:ss"
                                onFinish={onCountdownFinish}
                                valueStyle={{ fontSize: isMobile ? '16px' : '14px' }}
                            />
                        </Space>
                    ) : (
                        <Button
                            type="link"
                            onClick={handleResendOtp}
                            disabled={!canResend}
                            loading={isResendingPhoneOtp}
                            size={isMobile ? 'small' : 'middle'}
                            style={{ fontSize: isMobile ? '14px' : '16px' }}
                        >
                            Resend Verification Code
                        </Button>
                    )}

                    <div style={{
                        marginTop: isMobile ? '12px' : '16px',
                        padding: isMobile ? '12px' : '16px',
                        backgroundColor: '#f6ffed',
                        border: '1px solid #b7eb8f',
                        borderRadius: '6px'
                    }}>
                        <Space direction={isMobile ? 'vertical' : 'horizontal'} size="small">
                            <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                                <strong>OTP expires in:</strong>
                            </Text>
                            {otpExpiryCountdown !== null ? (
                                <Countdown
                                    value={otpExpiryCountdown}
                                    format="mm:ss"
                                    onFinish={() => setOtpExpiryCountdown(null)}
                                    valueStyle={{ fontSize: isMobile ? '14px' : '12px' }}
                                />
                            ) : (
                                <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '12px' }}>
                                    00:00
                                </Text>
                            )}
                        </Space>
                    </div>
                </div>
            </Form>
        </div>
    );
};

export default PhoneVerificationStep;
